import { RECEIPT_MAX_BYTES, RECEIPT_MAX_EDGE, fitWithin, uploadedImagePath } from '@mypoz/shared';

import { legacyUploadApi } from '@/lib/legacy-api.js';

/** Why an upload did not give back a path. */
export type UploadImageFailure =
  /** `-1`: the vendor did not accept the image format, or the browser could not decode it. */
  | 'format'
  /** Still over `RECEIPT_MAX_BYTES` after shrinking. */
  | 'size'
  /** `0`, a missing path, or a lost/timed-out request. */
  | 'failed';

export class UploadImageError extends Error {
  constructor(
    readonly failure: UploadImageFailure,
    readonly code: string,
    message: string,
  ) {
    super(`uploadImg failed (${code}): ${message}`);
    this.name = 'UploadImageError';
  }
}

/** The file as a `data:image/…;base64,…` string, which is what `uploadImg` expects. */
function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
}

/** JPEG quality for a shrunk receipt: small, and the printed text stays sharp. */
const SHRUNK_QUALITY = 0.85;

/**
 * The image redrawn at most `RECEIPT_MAX_EDGE` pixels on its longest side, as a JPEG (MYP-96).
 *
 * A phone photo of a receipt is often 4–12 MB; shrunk it is usually well under 1 MB, which is the
 * difference between an upload that finishes and one that times out, and keeps the request far
 * below whatever body limit the vendor's server has. The vendor saves every upload as `.jpg`
 * anyway (live, 2026-09-30: a PNG came back as `…_2144557528.jpg`). PNG transparency is flattened
 * onto white so it does not turn black. A file the browser cannot decode is `format`.
 */
async function shrinkImage(file: Blob): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch (cause) {
    throw new UploadImageError('format', 'DECODE', String(cause));
  }
  try {
    const { width, height } = fitWithin(bitmap.width, bitmap.height, RECEIPT_MAX_EDGE);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(bitmap, 0, 0, width, height);
    const shrunk = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', SHRUNK_QUALITY),
    );
    // Keep the original if redrawing somehow made it bigger (a tiny, already-compressed image).
    return shrunk !== null && shrunk.size < file.size ? shrunk : file;
  } finally {
    bitmap.close();
  }
}

/**
 * Uploads one image through `uploadImg` and returns the path the vendor stored it at
 * (`imgUrl` + `imgName`), e.g. `/Public/Uploads/2026-09-29/20260929120000_123.jpg` (MYP-96).
 *
 * Shared on purpose: Orders sends the path in `orders_create`'s `packageinfo`, and China Payment
 * (MYP-62) will send it as `chargehandle`'s `upfile`. The caller checks the file's type and size
 * first (`checkReceiptFile`), so nothing enormous is ever decoded; the image is then shrunk
 * (`shrinkImage`) before it is encoded and sent.
 */
export async function uploadImage(file: Blob): Promise<string> {
  const image = await shrinkImage(file);
  if (image.size > RECEIPT_MAX_BYTES) {
    throw new UploadImageError('size', 'SIZE', `Still ${image.size} bytes after shrinking`);
  }
  const dataUrl = await readAsDataUrl(image);
  const result = await legacyUploadApi.call<unknown>('uploadImg', { file: dataUrl });
  if (!result.ok) {
    throw new UploadImageError(
      result.code === '-1' ? 'format' : 'failed',
      result.code,
      result.message,
    );
  }
  const path = uploadedImagePath(result.data);
  if (path === undefined) throw new UploadImageError('failed', 'MALFORMED', 'No imgUrl/imgName');
  return path;
}
